import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash, Lightning, EnvelopeSimple, ArrowsClockwise, LinkSimpleBreak, PhoneCall, HandTap, Users } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { useLeadsTeam } from "../lib/leadsTeam";
import { PillTabs } from "../components/lux";
import { MyLeadsPanel } from "../components/leads/MyLeadsPanel";
import { WebsiteLeadsTable } from "../components/leads/WebsiteLeadsTable";
import { AllLeadsTable } from "../components/leads/AllLeadsTable";
import { WebsiteRequestBlock } from "../components/leads/WebsiteRequestBlock";
import { LeadsFilterBar, emptyFilters, matchesFilters } from "../components/leads/LeadsFilterBar";

const PAGE_SIZE = 50;

const toLocalInput = (iso) => {
  if (!iso) return "";
  // A date-only value ("2026-09-10") must be read as LOCAL midnight, otherwise
  // JS parses it as UTC and the input shows the previous day.
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00` : iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function GmailLeadsBanner({ onSynced }) {
  const [status, setStatus] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try { const { data } = await api.get("/integrations/gmail/status"); setStatus(data); }
    catch (e) { if (e?.response?.status === 403) setHidden(true); }
  };
  useEffect(() => { loadStatus(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/integrations/gmail/connect");
      window.open(data.auth_url, "_blank", "noopener");
      toast.info("Authorize 01 Auto Group in the new tab, then click 'Refresh status'.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/gmail/sync");
      toast.success(`Synced — ${data.imported} new lead(s) imported.`);
      loadStatus(); onSynced?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Gmail? Leads will stop auto-importing.")) return;
    setBusy(true);
    try { await api.post("/integrations/gmail/disconnect"); toast.success("Gmail disconnected"); loadStatus(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (hidden || !status || !status.configured) return null;

  return (
    <div className="border border-border bg-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="gmail-integration-banner">
      <div className="flex items-center gap-3">
        <EnvelopeSimple size={22} className="text-primary" />
        <div>
          <div className="font-heading text-sm">Shopify lead auto-import {status.connected ? "· Active" : ""}</div>
          <div className="text-xs text-muted-foreground">
            {status.connected
              ? `Connected: ${status.email || "inbox"}${status.last_sync ? ` · Last sync ${new Date(status.last_sync).toLocaleString()}` : ""} · ${status.imported_total || 0} imported`
              : "Connect your Gmail to pull contact-form leads automatically every 10 minutes."}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {status.connected ? (
          <>
            <Button size="sm" variant="outline" onClick={loadStatus} disabled={busy} data-testid="gmail-refresh-status">Refresh status</Button>
            <Button size="sm" variant="outline" onClick={syncNow} disabled={busy} data-testid="gmail-sync-now"><ArrowsClockwise size={14} className="mr-1.5" />Sync now</Button>
            <Button size="sm" variant="ghost" onClick={disconnect} disabled={busy} data-testid="gmail-disconnect"><LinkSimpleBreak size={14} className="text-destructive" /></Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={connect} disabled={busy} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="gmail-connect">Connect Gmail</Button>
            <Button size="sm" variant="outline" onClick={loadStatus} disabled={busy} data-testid="gmail-refresh-status">Refresh status</Button>
          </>
        )}
      </div>
    </div>
  );
}

const serviceOptions = [
  { value: "rental", label: "Rental" },
  { value: "chauffeur", label: "Chauffeur" },
  { value: "tour", label: "Tour" },
];

const statusOptions = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "quoted", label: "Quoted" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
];

const FIELDS = [
  { name: "customer_name", label: "Customer name", required: true },
  { name: "service_type", label: "Service", type: "select", options: serviceOptions, default: "rental" },
  { name: "customer_email", label: "Email" },
  { name: "customer_phone", label: "Phone" },
  { name: "salesperson", label: "Salesperson (who contacted)", type: "select", options: [] },
  { name: "contacted_at", label: "Time contacted (auto-set to now if left blank)", type: "datetime" },
  { name: "pickup_date", label: "Pickup", type: "datetime" },
  { name: "return_date", label: "Return", type: "datetime" },
  { name: "pickup_location", label: "Pickup location", span: 2 },
  { name: "dropoff_location", label: "Dropoff location", span: 2 },
  { name: "vehicle_preference", label: "Vehicle preference" },
  { name: "quoted_amount", label: "Quoted amount", type: "number" },
  { name: "next_follow_up_at", label: "Next follow-up", type: "datetime" },
  { name: "status", label: "Status", type: "select", options: statusOptions, default: "new" },
  { name: "source", label: "Source (e.g. Instagram, Referral)" },
  { name: "notes", label: "Notes", type: "textarea", span: 2 },
];

export default function Leads() {
  const { user } = useAuth();
  const leadsTeam = useLeadsTeam();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "all" ? "all" : "mine";
  const isAdmin = user?.role === "admin";

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [dialogLoading, setDialogLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [selected, setSelected] = useState([]);
  const [selecting, setSelecting] = useState(false);          // UI-002: tick boxes show only while selecting
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [editingCell, setEditingCell] = useState(null);       // { id, field } — one dropdown at a time, page-wide
  const [highlightId, setHighlightId] = useState(null);
  const [minePins, setMinePins] = useState({});               // My Leads sticky overlay, survives tab switches
  const navigate = useNavigate();

  // Badge count for the tab. Shares the ["leads-mine"] cache with MyLeadsPanel.
  const { data: queue } = useQuery({
    queryKey: ["leads-mine"],
    queryFn: async () => (await api.get("/leads/mine")).data,
  });

  // SALES-031: the full list is fetched ONLY when the All-leads tab is open, and
  // cached (2-min stale) so reopening it — or coming back to the page — is
  // instant. On refetch the rows stay on screen; "Loading…" is only the very
  // first load. The true total comes back in the X-Total-Count header.
  const allLeads = useQuery({
    queryKey: ["leads-all"],
    enabled: tab === "all",
    staleTime: 2 * 60 * 1000,
    queryFn: async () => {
      const res = await api.get("/leads");
      return { rows: res.data, total: Number(res.headers["x-total-count"] ?? res.data.length) };
    },
  });
  const rows = useMemo(() => allLeads.data?.rows || [], [allLeads.data]);
  const total = allLeads.data?.total ?? rows.length;

  const tabs = [
    { key: "mine", label: "My Leads", icon: HandTap, count: queue?.stats?.needs_reply || 0 },
    { key: "all", label: "All leads", icon: Users },
  ];
  const setTab = (t) => setParams({ tab: t }, { replace: true });

  const refreshQueue = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["leads-mine"] });
    qc.invalidateQueries({ queryKey: ["leads-unassigned"] });
    qc.invalidateQueries({ queryKey: ["notifications-feed"] });
  }, [qc]);

  // ---- All-leads derived views (all recompute only when data/filters change,
  // never on dialog typing) ----
  const websiteLeads = useMemo(() => rows.filter((l) => l.origin === "website" && matchesFilters(l, filters)), [rows, filters]);
  const otherLeads = useMemo(() => rows.filter((l) => l.origin !== "website" && matchesFilters(l, filters)), [rows, filters]);
  const sources = useMemo(() => [...new Set(rows.map((l) => (l.source || "").trim()).filter(Boolean))].sort(), [rows]);
  const visibleOther = useMemo(() => otherLeads.slice(0, visibleCount), [otherLeads, visibleCount]);
  const hasMore = otherLeads.length > visibleOther.length;
  const allSelected = otherLeads.length > 0 && otherLeads.every((l) => selected.includes(l.id));

  // Changing a filter starts the scroll again from the top 50.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filters]);

  const otherRef = useRef(otherLeads);
  otherRef.current = otherLeads;

  // ---- stable handlers (so the memoized tables skip re-render on dialog typing) ----
  const onLoadMore = useCallback(() => setVisibleCount((c) => c + PAGE_SIZE), []);
  const onStartEdit = useCallback((id, field) => setEditingCell({ id, field }), []);
  const onCancelEdit = useCallback(() => setEditingCell(null), []);
  const toggleRow = useCallback((id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])), []);
  const toggleAll = useCallback(() => setSelected((s) => {
    const ids = otherRef.current.map((r) => r.id);
    return ids.every((id) => s.includes(id)) ? s.filter((id) => !ids.includes(id)) : [...new Set([...s, ...ids])];
  }), []);
  const clearSelection = useCallback(() => setSelected([]), []);
  // Entering and leaving select mode both start from nothing ticked, so a
  // selection can never be held out of sight.
  const toggleSelecting = useCallback(() => { setSelected([]); setSelecting((v) => !v); }, []);

  const patchRow = useCallback((lead) => {
    qc.setQueryData(["leads-all"], (old) => (old ? { ...old, rows: old.rows.map((r) => (r.id === lead.id ? { ...r, ...lead } : r)) } : old));
  }, [qc]);
  const removeRow = useCallback((id) => {
    qc.setQueryData(["leads-all"], (old) => (old ? { ...old, rows: old.rows.filter((r) => r.id !== id) } : old));
  }, [qc]);
  const flashRow = useCallback((id) => {
    setHighlightId(id);
    setTimeout(() => setHighlightId((h) => (h === id ? null : h)), 1600);
  }, []);
  // Create / bulk / Gmail-sync: the only paths that refetch the whole list.
  const reloadAll = useCallback(() => { qc.invalidateQueries({ queryKey: ["leads-all"] }); refreshQueue(); }, [qc, refreshQueue]);

  // One committed change on an inline cell → save exactly as before, patch that
  // one row from the fresh lead, and leave the scroll where it is.
  const onCommitCell = useCallback(async (id, field, value) => {
    setEditingCell(null);
    try {
      if (field === "assigned") {
        const { data } = await api.post(`/leads/${id}/assign`, { salesperson: value });
        patchRow(data);
      } else {
        // Status keeps calling /leads/bulk exactly as today (incl. lost-reason),
        // then reads the fresh lead to patch the row in place.
        if (value === "lost") await api.post("/leads/bulk", { ids: [id], action: "lost", lost_reason: "Other" });
        else await api.post("/leads/bulk", { ids: [id], action: "status", status: value });
        const { data } = await api.get(`/leads/${id}`);
        patchRow(data);
      }
      flashRow(id);
      refreshQueue();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [patchRow, flashRow, refreshQueue]);

  const onContacted = useCallback(async (id) => {
    try { const { data } = await api.post(`/leads/${id}/contact`); toast.success("Marked contacted"); patchRow(data); flashRow(id); refreshQueue(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [patchRow, flashRow, refreshQueue]);

  const onConvert = useCallback(async (id) => {
    try {
      await api.post(`/leads/${id}/convert`);
      toast.success("Converted — the booking is now in Rental ▸ Pre-bookings");
      const { data } = await api.get(`/leads/${id}`);
      patchRow(data); flashRow(id); refreshQueue();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [patchRow, flashRow, refreshQueue]);

  const onDelete = useCallback(async (id) => {
    if (!confirm("Delete this lead?")) return;
    try { await api.delete(`/leads/${id}`); toast.success("Deleted"); removeRow(id); refreshQueue(); }
    catch (e) { toast.error(formatApiError(e)); }
  }, [removeRow, refreshQueue]);

  const onOpenBooking = useCallback((bid) => navigate(`/bookings/view/${bid}`), [navigate]);

  // ---- dialog ----
  const openNew = () => {
    setEditing(null);
    setForm(Object.fromEntries(FIELDS.map((f) => [f.name, f.default ?? (f.type === "number" ? 0 : "")])));
    setDialogLoading(false);
    setOpen(true);
  };
  const openEdit = useCallback(async (stub) => {
    setEditing(stub);
    setForm({});
    setDialogLoading(true);
    setOpen(true);
    try { const { data } = await api.get(`/leads/${stub.id}`); setForm({ ...data }); }
    catch (e) { toast.error(formatApiError(e)); setOpen(false); }
    finally { setDialogLoading(false); }
  }, []);

  const fields = useMemo(() => {
    const names = [...leadsTeam];
    const saved = (form.salesperson || "").trim();
    if (saved && !names.some((n) => n.toLowerCase() === saved.toLowerCase())) names.unshift(saved);
    const options = names.map((n) => ({ value: n, label: n }));
    return FIELDS.map((f) => (f.name === "salesperson" ? { ...f, options } : f));
  }, [leadsTeam, form.salesperson]);

  const submit = async () => {
    setSaving(true);
    try {
      // Only the fields the dialog loaded/edits are sent, so a partial save can
      // never blank notes, website_details, origin or the tracking stamps.
      const payload = {};
      for (const f of FIELDS) {
        let v = form[f.name];
        if (f.type === "number" && v !== "" && v != null) v = Number(v) || 0;
        payload[f.name] = v;
      }
      if (payload.salesperson && !payload.contacted_at) payload.contacted_at = new Date().toISOString();
      if (editing) {
        const { data } = await api.put(`/leads/${editing.id}`, payload);
        toast.success("Updated");
        patchRow(data);
        refreshQueue();
      } else {
        await api.post("/leads", payload);
        toast.success("Lead added");
        reloadAll();
      }
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const truncated = total > rows.length;

  return (
    <div className="space-y-8" data-testid="leads-page">
      <GmailLeadsBanner onSynced={reloadAll} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillTabs tabs={tabs} value={tab} onChange={setTab} testid="leads-tabs" />
        <div className="flex items-center gap-2">
          {tab === "all" && (
            <Button variant="outline" onClick={toggleSelecting} aria-pressed={selecting}
              className={`hidden md:inline-flex ${selecting ? "border-primary/40 text-primary hover:text-primary" : ""}`}
              data-testid="leads-select-toggle">
              {selecting ? "Done" : "Select"}
            </Button>
          )}
          <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="leads-create-button">
            <Plus size={16} className="mr-1.5" />New lead
          </Button>
        </div>
      </div>

      {tab === "mine" ? (
        <MyLeadsPanel onEdit={openEdit} pins={minePins} setPins={setMinePins} />
      ) : (
        <>
          <LeadsFilterBar
            filters={filters}
            setFilters={setFilters}
            sources={sources}
            team={leadsTeam}
            selected={selected}
            clearSelection={clearSelection}
            onDone={() => { clearSelection(); setSelecting(false); reloadAll(); }}
          />

          <WebsiteLeadsTable
            rows={websiteLeads}
            loading={allLeads.isLoading}
            team={leadsTeam}
            isAdmin={isAdmin}
            selecting={selecting}
            selected={selected}
            toggleRow={toggleRow}
            editingCell={editingCell}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onCommit={onCommitCell}
            onConvert={onConvert}
            onEdit={openEdit}
            onDelete={onDelete}
            onOpenBooking={onOpenBooking}
            highlightId={highlightId}
          />

          {truncated && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] px-4 py-3 text-sm text-amber-300" data-testid="leads-truncation-notice">
              Showing the newest {rows.length.toLocaleString()} of {total.toLocaleString()} leads. Older leads are hidden.
            </div>
          )}

          <AllLeadsTable
            rows={visibleOther}
            totalMatching={otherLeads.length}
            shownCount={visibleOther.length}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            loading={allLeads.isLoading}
            isError={allLeads.isError}
            team={leadsTeam}
            selecting={selecting}
            selected={selected}
            allSelected={allSelected}
            toggleRow={toggleRow}
            toggleAll={toggleAll}
            isAdmin={isAdmin}
            editingCell={editingCell}
            onStartEdit={onStartEdit}
            onCancelEdit={onCancelEdit}
            onCommit={onCommitCell}
            onContacted={onContacted}
            onConvert={onConvert}
            onEdit={openEdit}
            onDelete={onDelete}
            onOpenBooking={onOpenBooking}
            highlightId={highlightId}
          />
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? ((editing.origin || form.origin) === "website" ? "Edit website lead" : "Edit lead") : "New lead"}</DialogTitle>
            <DialogDescription>Update the lead, then save. Convert it once the client is ready to book.</DialogDescription>
          </DialogHeader>
          {dialogLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground" data-testid="lead-dialog-loading">Loading the full lead…</div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <WebsiteRequestBlock details={form?.website_details} />
              {fields.map((f) => (
                <div key={f.name} className={f.span === 2 ? "col-span-2" : "col-span-2 md:col-span-1"}>
                  <Label className="label-mono mb-1.5 block">{f.label}{f.required && <span className="text-primary"> *</span>}</Label>
                  {f.type === "select" ? (
                    <Select value={form[f.name] || ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                      <SelectTrigger className="bg-input border-border" data-testid={`leads-field-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea rows={3} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="bg-input border-border" data-testid={`leads-field-${f.name}`} />
                  ) : f.type === "datetime" ? (
                    <Input type="datetime-local" step={300} value={toLocalInput(form[f.name])} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="bg-input border-border" data-testid={`leads-field-${f.name}`} />
                  ) : (
                    <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="bg-input border-border" data-testid={`leads-field-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving || dialogLoading} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="leads-dialog-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
